import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppShell, PageContainer } from '../../features/ui-system';
import {
  AlertTriangle, CheckCircle2, ClipboardList, RefreshCw, UserCheck
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import SearchableComboBox from '../../components/SearchableComboBox';
import StaffPageLoadingOverlay from '../../components/StaffPageLoadingOverlay';
import StaffPageHeader from '../../components/StaffPageHeader';
import { resolveBackendBaseUrl } from '../../utils/runtimeConfig';

const API = `${resolveBackendBaseUrl()}/api`;
const BASE_URL = `${resolveBackendBaseUrl()}`.replace(/\/$/, '');

const doneStatuses = new Set(['completed', 'needs_special_handling']);

const getTicketStatusLabel = (status) => {
  if (status === 'pending_approval') return 'Menunggu Approval Teknisi';
  if (status === 'approved') return 'Teknisi Turun Lapangan';
  if (status === 'completed') return 'Selesai';
  if (status === 'needs_special_handling') return 'Butuh Penanganan Khusus';
  return 'Tidak diketahui';
};

const getTicketStatusClass = (status) => {
  if (status === 'completed') return 'text-emerald-700';
  if (status === 'needs_special_handling') return 'text-rose-700';
  if (status === 'approved') return 'text-cyan-700';
  if (status === 'pending_approval') return 'text-amber-700';
  return 'text-slate-500';
};

const AdminFieldHandling = () => {
  const [customers, setCustomers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: '',
    issue_notes: '',
    requested_technician_ids: [],
  });
  const [openTicketPage, setOpenTicketPage] = useState(1);
  const [completedTicketPage, setCompletedTicketPage] = useState(1);
  const [ticketPerPage, setTicketPerPage] = useState(10);
  const [ticketSearch, setTicketSearch] = useState('');

  const getHeaders = () => {
    const token = localStorage.getItem('ktc_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const headers = { headers: getHeaders() };
      const [customersRes, techniciansRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/customers`, headers),
        axios.get(`${API}/users`, { ...headers, params: { role: 'technician' } }),
        axios.get(`${API}/field-tickets`, headers),
      ]);

      setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
      setTechnicians(Array.isArray(techniciansRes.data) ? techniciansRes.data : []);
      setTickets(Array.isArray(ticketsRes.data) ? ticketsRes.data : []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memuat data penanganan pelanggan');
      setCustomers([]);
      setTechnicians([]);
      setTickets([]);
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  const toggleTechnician = (technicianId) => {
    setFormData((prev) => {
      const alreadySelected = prev.requested_technician_ids.includes(technicianId);
      if (alreadySelected) {
        return {
          ...prev,
          requested_technician_ids: prev.requested_technician_ids.filter((id) => id !== technicianId),
        };
      }
      return {
        ...prev,
        requested_technician_ids: [...prev.requested_technician_ids, technicianId],
      };
    });
  };

  const handleCreateTicket = async (event) => {
    event.preventDefault();
    if (!formData.customer_id) {
      toast.error('Pilih pelanggan terlebih dahulu');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/field-tickets`,
        {
          customer_id: formData.customer_id,
          issue_notes: formData.issue_notes.trim() || null,
          requested_technician_ids: formData.requested_technician_ids,
        },
        { headers: getHeaders() }
      );

      toast.success('Tiket penanganan berhasil dibuat');
      setFormData({ customer_id: '', issue_notes: '', requested_technician_ids: [] });
      fetchData(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membuat tiket penanganan');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredTickets = useMemo(() => {
    const q = String(ticketSearch || '').trim().toLowerCase();
    if (!q) return tickets;
    return tickets.filter((ticket) =>
      `${ticket.customer_name || ''} ${ticket.customer_code || ''} ${ticket.customer_phone || ''} ${ticket.odp || ''} ${ticket.issue_notes || ''} ${(ticket.approved_technician_names || []).join(' ')}`
        .toLowerCase()
        .includes(q),
    );
  }, [ticketSearch, tickets]);
  const openTickets = useMemo(() => filteredTickets.filter((ticket) => !doneStatuses.has(ticket.status)), [filteredTickets]);
  const completedTickets = useMemo(() => filteredTickets.filter((ticket) => doneStatuses.has(ticket.status)), [filteredTickets]);
  const totalOpenPages = Math.max(1, Math.ceil(openTickets.length / ticketPerPage));
  const totalCompletedPages = Math.max(1, Math.ceil(completedTickets.length / ticketPerPage));
  const pagedOpenTickets = useMemo(() => {
    const start = (openTicketPage - 1) * ticketPerPage;
    return openTickets.slice(start, start + ticketPerPage);
  }, [openTickets, openTicketPage, ticketPerPage]);
  const pagedCompletedTickets = useMemo(() => {
    const start = (completedTicketPage - 1) * ticketPerPage;
    return completedTickets.slice(start, start + ticketPerPage);
  }, [completedTickets, completedTicketPage, ticketPerPage]);
  useEffect(() => {
    setOpenTicketPage(1);
    setCompletedTicketPage(1);
  }, [ticketPerPage, tickets.length]);
  useEffect(() => {
    if (openTicketPage > totalOpenPages) setOpenTicketPage(totalOpenPages);
  }, [openTicketPage, totalOpenPages]);
  useEffect(() => {
    if (completedTicketPage > totalCompletedPages) setCompletedTicketPage(totalCompletedPages);
  }, [completedTicketPage, totalCompletedPages]);
  const customerOptions = useMemo(
    () =>
      customers.map((customer) => ({
        value: String(customer.id),
        label: `${customer.customer_id || '-'} - ${customer.name || '-'}`,
        description: `${customer.phone || '-'} | ODP: ${customer.odp || '-'}`,
        keywords: `${customer.customer_id || ''} ${customer.name || ''} ${customer.phone || ''} ${customer.odp || ''}`,
      })),
    [customers]
  );

  return (
    <>
      {loading && (
        <StaffPageLoadingOverlay text="Memuat data penanganan..." />
      )}
      <AppShell>
      <PageContainer className="cc-legacy-surface">
        <StaffPageHeader
          eyebrow="Penanganan"
          title="Penanganan Pelanggan"
          subtitle="Buat tiket perbaikan, assign teknisi, dan pantau tiket selesai."
          actions={(
            <Button onClick={() => fetchData(true)} variant="outline" className="btn-secondary" disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Muat Ulang
            </Button>
          )}
        />
        <section className="staff-card p-4">
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_180px]">
            <Input
              value={ticketSearch}
              onChange={(event) => setTicketSearch(event.target.value)}
              placeholder="Cari tiket: nama pelanggan, ID, ODP, teknisi..."
              className="bg-white border-slate-200 text-slate-900"
            />
            <select
              value={String(ticketPerPage)}
              onChange={(e) => setTicketPerPage(Number(e.target.value))}
              className="h-10 rounded-md border border-slate-200 bg-white px-3 text-slate-900"
            >
              <option value="10">10 / halaman</option>
              <option value="20">20 / halaman</option>
              <option value="50">50 / halaman</option>
            </select>
          </div>
        </section>

        <div className="grid xl:grid-cols-3 gap-6">
          <div className="xl:col-span-1 staff-card rounded-xl border p-5">
            <h2 className="text-slate-900 font-semibold mb-4 flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />Buat Tiket Baru
            </h2>

            <form onSubmit={handleCreateTicket} className="space-y-4">
              <div>
                <label className="staff-form-label">Pelanggan</label>
                <SearchableComboBox
                  value={formData.customer_id || ''}
                  onChange={(value) => setFormData((prev) => ({ ...prev, customer_id: value || '' }))}
                  options={customerOptions}
                  placeholder="Pilih pelanggan"
                  searchPlaceholder="Cari ID / nama / nomor HP / ODP..."
                  emptyLabel="Pelanggan tidak ditemukan"
                  minSearchChars={1}
                  className="bg-white border-slate-200 text-slate-900 hover:bg-slate-50"
                />
              </div>

              <div>
                <label className="staff-form-label">Keluhan / Catatan</label>
                <Textarea
                  value={formData.issue_notes}
                  onChange={(event) => setFormData((prev) => ({ ...prev, issue_notes: event.target.value }))}
                  rows={3}
                  className="bg-white border-slate-200 text-slate-900 resize-none"
                  placeholder="Contoh: ONU sering loss sejak kemarin malam"
                />
              </div>

              <div>
                <label className="staff-form-label">Rekomendasi Teknisi</label>
                <div className="space-y-2 max-h-48 overflow-auto pr-1">
                  {technicians.length === 0 && (
                    <p className="text-slate-500 text-xs">Belum ada user role teknisi.</p>
                  )}

                  {technicians.map((technician) => {
                    const checked = formData.requested_technician_ids.includes(technician.id);
                    return (
                      <label key={technician.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 cursor-pointer">
                        <div>
                          <p className="text-slate-900 text-sm">{technician.name}</p>
                          <p className="text-slate-500 text-xs">{technician.username}</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleTechnician(technician.id)}
                          className="h-4 w-4 accent-brand-blue"
                        />
                      </label>
                    );
                  })}
                </div>
              </div>

              <Button type="submit" className="btn-primary text-white w-full" disabled={submitting}>
                {submitting ? 'Menyimpan tiket...' : 'Buat Tiket Penanganan'}
              </Button>
            </form>
          </div>

          <div className="xl:col-span-2 space-y-6">
            <div className="staff-card rounded-xl border p-5">
              <h2 className="text-slate-900 font-semibold mb-4 flex items-center gap-2">
                <UserCheck className="w-4 h-4" />Tiket Berjalan ({openTickets.length})
              </h2>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#dbe7ff] bg-white px-3 py-2 text-xs">
                <p className="text-slate-600">Menampilkan {(openTicketPage - 1) * ticketPerPage + 1}-{Math.min(openTicketPage * ticketPerPage, openTickets.length)} dari {openTickets.length}</p>
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" variant="outline" className="h-8 border-[#cbd5e1] bg-white px-3 text-xs text-slate-900 hover:bg-slate-50" disabled={openTicketPage <= 1} onClick={() => setOpenTicketPage(1)}>First</Button>
                  <Button type="button" size="sm" variant="outline" className="btn-secondary" disabled={openTicketPage <= 1} onClick={() => setOpenTicketPage((p) => Math.max(1, p - 1))}>Prev</Button>
                  <span className="text-slate-700">Hal {openTicketPage}/{totalOpenPages}</span>
                  <Button type="button" size="sm" variant="outline" className="btn-secondary" disabled={openTicketPage >= totalOpenPages} onClick={() => setOpenTicketPage((p) => Math.min(totalOpenPages, p + 1))}>Next</Button>
                  <Button type="button" size="sm" variant="outline" className="h-8 border-[#cbd5e1] bg-white px-3 text-xs text-slate-900 hover:bg-slate-50" disabled={openTicketPage >= totalOpenPages} onClick={() => setOpenTicketPage(totalOpenPages)}>Last</Button>
                </div>
              </div>

              <div className="space-y-3 max-h-[36vh] overflow-auto pr-1">
                {openTickets.length === 0 && (
                  <p className="text-slate-500 text-sm">Tidak ada tiket berjalan.</p>
                )}

                {pagedOpenTickets.map((ticket) => (
                  <div key={ticket.id} className="rounded-lg border border-slate-200 bg-white p-4 space-y-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div>
                        <p className="text-slate-900 font-semibold">{ticket.customer_name}</p>
                        <p className="text-xs text-slate-500">{ticket.customer_code || '-'} | {ticket.customer_phone || '-'}</p>
                      </div>
                      <p className={`text-xs font-semibold ${getTicketStatusClass(ticket.status)}`}>{getTicketStatusLabel(ticket.status)}</p>
                    </div>
                    <p className="text-xs text-slate-500">ODP: {ticket.odp || '-'}</p>
                    <p className="text-xs text-slate-500">Pelapor: {ticket.reported_by_role === 'customer' ? 'Portal Pelanggan' : 'Admin'} - {ticket.reported_by_name || '-'}</p>
                    {ticket.issue_notes && <p className="text-sm text-slate-700">Keluhan: {ticket.issue_notes}</p>}
                    <p className="text-xs text-slate-500">Rekomendasi Teknisi: {(ticket.requested_technician_names || []).join(', ') || '-'}</p>
                    <p className="text-xs text-slate-500">Teknisi Approve: {(ticket.approved_technician_names || []).join(', ') || '-'}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="staff-card rounded-xl border p-5">
              <h2 className="text-slate-900 font-semibold mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />Riwayat Selesai ({completedTickets.length})
              </h2>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#dbe7ff] bg-white px-3 py-2 text-xs">
                <p className="text-slate-600">Menampilkan {(completedTicketPage - 1) * ticketPerPage + 1}-{Math.min(completedTicketPage * ticketPerPage, completedTickets.length)} dari {completedTickets.length}</p>
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" variant="outline" className="h-8 border-[#cbd5e1] bg-white px-3 text-xs text-slate-900 hover:bg-slate-50" disabled={completedTicketPage <= 1} onClick={() => setCompletedTicketPage(1)}>First</Button>
                  <Button type="button" size="sm" variant="outline" className="btn-secondary" disabled={completedTicketPage <= 1} onClick={() => setCompletedTicketPage((p) => Math.max(1, p - 1))}>Prev</Button>
                  <span className="text-slate-700">Hal {completedTicketPage}/{totalCompletedPages}</span>
                  <Button type="button" size="sm" variant="outline" className="btn-secondary" disabled={completedTicketPage >= totalCompletedPages} onClick={() => setCompletedTicketPage((p) => Math.min(totalCompletedPages, p + 1))}>Next</Button>
                  <Button type="button" size="sm" variant="outline" className="h-8 border-[#cbd5e1] bg-white px-3 text-xs text-slate-900 hover:bg-slate-50" disabled={completedTicketPage >= totalCompletedPages} onClick={() => setCompletedTicketPage(totalCompletedPages)}>Last</Button>
                </div>
              </div>

              <div className="space-y-3 max-h-[36vh] overflow-auto pr-1">
                {completedTickets.length === 0 && (
                  <p className="text-slate-500 text-sm">Belum ada tiket selesai.</p>
                )}

                {pagedCompletedTickets.map((ticket) => (
                  <div key={ticket.id} className="rounded-lg border border-slate-200 bg-white p-4 space-y-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div>
                        <p className="text-slate-900 font-semibold">{ticket.customer_name}</p>
                        <p className="text-xs text-slate-500">{ticket.customer_code || '-'} | {ticket.customer_phone || '-'}</p>
                      </div>
                      <p className={`text-xs font-semibold ${getTicketStatusClass(ticket.status)}`}>{getTicketStatusLabel(ticket.status)}</p>
                    </div>

                    <p className="text-xs text-slate-500">Pelapor: {ticket.reported_by_role === 'customer' ? 'Portal Pelanggan' : 'Admin'} - {ticket.reported_by_name || '-'}</p>
                    <p className="text-xs text-slate-500">Teknisi Penyelesai: {(ticket.completed_technician_names || []).join(', ') || '-'}</p>
                    <p className="text-xs text-slate-500">Selesai: {ticket.completed_at ? new Date(ticket.completed_at).toLocaleString('id-ID') : '-'}</p>
                    {ticket.completion_notes && (
                      <p className="text-sm text-slate-700">Catatan Penyelesaian: {ticket.completion_notes}</p>
                    )}
                    {Array.isArray(ticket.evidence) && ticket.evidence.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {ticket.evidence.slice(0, 4).map((item) => (
                          <a key={item.id || item.url} href={`${BASE_URL}${item.url}`} target="_blank" rel="noreferrer" className="block h-14 w-14 overflow-hidden rounded border border-slate-200 bg-slate-50">
                            <img src={`${BASE_URL}${item.url}`} alt={item.original_name || 'Bukti pekerjaan'} className="h-full w-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                    {ticket.needs_special_handling && (
                      <p className="text-xs text-red-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Butuh penanganan khusus lanjutan</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
                  </PageContainer>
    </AppShell>
    </>
  );
};

export default AdminFieldHandling;



















